/**
 * /customer/claim-stamp/:brandId — Step 2: Upload Receipt (Phase R3).
 *
 * Mirrors the mobile `(customer)/claim-stamp/[brandId].tsx` screen —
 * tips card + two CTAs ("Take Photo" uses the device camera via the
 * input `capture` attribute, "Choose from Gallery" opens the picker).
 * Both CTAs trigger the same POST handler.
 *
 * On submit:
 *   1. POST /api/customer/receipt-claims (multipart) with brand_id.
 *   2. Backend returns a claim doc in `pending_ai_analysis`.
 *   3. Phase R7 (2026-07-01): BEFORE navigating to the Review screen,
 *      ask the server if a Google-Review prompt should fire for this
 *      brand with trigger_event=session_open. If yes, mount the
 *      ReviewPromptModal with operator-approved R7 copy. Dedup is
 *      localStorage-keyed by claim.id. Dismissing OR clicking routes
 *      to the Review screen next; the backend AI analysis + owner
 *      review run in the background regardless.
 *   4. Navigate to /customer/claim-stamp/review/{claimId} where the
 *      Review screen polls until AI OCR finishes.
 *
 * NON-NEGOTIABLE #1: AI never auto-awards. Only an explicit owner
 * approve action mints the stamp (R1 contract). The customer-facing
 * copy here warns about pending brand verification.
 */
import { useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Camera, Images, ChevronLeft, ChevronRight, Receipt, Clock,
  CheckCircle2, Loader2, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { claimErrorTitleAndBody } from '@/lib/claimErrorUI';
import ReviewPromptModal from '@/components/customer/ReviewPromptModal';

const TIPS = [
  'Show the full receipt in frame',
  'Make sure the text is readable',
  'Include the date and total amount',
  'Avoid blurry or dark photos',
];

// Phase R7 (2026-07-01) — localStorage key format for per-claim dedup.
const R7_DEDUP_PREFIX = 'onestamp.review_popup_shown.';

export default function CustomerClaimStampUploadPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const [submitting, setSubmitting] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // Phase R7 — modal lifecycle state. `pendingClaimId` is the route we'll
  // navigate to once the user dismisses or clicks the modal.
  const [reviewPrompt, setReviewPrompt] = useState(null);
  const [pendingClaimId, setPendingClaimId] = useState(null);

  const brandQ = useQuery({
    queryKey: qk.customerBrand(brandId),
    queryFn: () => api.customerBrand(token, brandId),
    enabled: !!token && !!brandId,
  });
  const brand = brandQ.data;

  const navigateToReview = (claimId) => {
    navigate(`/customer/claim-stamp/review/${claimId}`, { replace: true });
  };

  /**
   * Phase R7 — fire the review-prompt modal if the server greenlights
   * one AND we haven't already shown one for this claim on this device.
   * Fire-and-forget: any failure (network, 404, show:false, dedup hit)
   * silently routes to the Review screen. We NEVER block the submit
   * success flow on the prompt.
   *
   * trigger_event discovery (2026-07-01): the sealed `rewards.py`
   * `/should-show` rule evaluator gates every rule type on either
   * `stamp_earned` or `reward_redeemed` — `session_open` is in the
   * enum but has no matching rule branch and ALWAYS returns
   * {show:false,reason:"rule_event_mismatch"}. We therefore send
   * `stamp_earned` so brands with `every_n_visits` / `after_n_stamps`
   * frequency rules can greenlight the prompt. The semantic gap
   * (receipt-submit vs. stamp-earned) is covered by the client-side
   * `console.info` distinguisher below — operator-approved fallback.
   */
  const maybeTriggerReviewPrompt = async (claim) => {
    try {
      // Dedup check — localStorage per claim.id.
      const dedupKey = `${R7_DEDUP_PREFIX}${claim.id}`;
      if (window.localStorage?.getItem(dedupKey)) {
        navigateToReview(claim.id);
        return;
      }
      // Server-side gate (R7 2026-07-01 — dedicated trigger wired through
      // `rewards.py` → `_r7_receipt_submit_evaluator` added as pure addition;
      // honors existing opt-out + 7d/30d cooldowns).
      const resp = await api.reviewPromptShouldShow(token, {
        brandId: claim.brand_id,
        triggerEvent: 'receipt_submitted',
      });
      if (!resp?.show) {
        navigateToReview(claim.id);
        return;
      }
      // Operator-approved analytics distinguisher (backend `/track`
      // schema is strict `{review_link_id, action}` so metadata cannot
      // be sent inline — client-side log is the sanctioned fallback).
      // eslint-disable-next-line no-console
      console.info('[r7] review_prompt_submit', {
        source: 'receipt_claim_submit',
        claim_id: claim.id,
        brand_id: claim.brand_id,
      });
      setReviewPrompt(resp);
      setPendingClaimId(claim.id);
    } catch {
      // Any upstream failure → silently fall through to the Review screen.
      navigateToReview(claim.id);
    }
  };

  const handleReviewPromptClose = () => {
    if (pendingClaimId) {
      try {
        window.localStorage?.setItem(
          `${R7_DEDUP_PREFIX}${pendingClaimId}`,
          new Date().toISOString(),
        );
      } catch { /* storage disabled → next load falls back to server dedup */ }
    }
    const dest = pendingClaimId;
    setReviewPrompt(null);
    setPendingClaimId(null);
    if (dest) navigateToReview(dest);
  };

  const handleFile = async (file) => {
    if (!file || !brandId || submitting) return;
    setSubmitting(true);
    let claim = null;
    try {
      claim = await api.customerCreateReceiptClaim(token, brandId, file);
    } catch (e) {
      // Edge-case gates — any 429/413/415/4xx surfaces here BEFORE the
      // review prompt is considered, so the popup never fires on a
      // failed submission.
      const { title, body } = claimErrorTitleAndBody(e?.code, e?.detail);
      toast.error(title, { description: body });
      setSubmitting(false);
      return;
    } finally {
      // Reset refs so picking the same file twice still fires `onChange`.
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
    // Edge-case: if the server auto-rejected this claim via R1 dedup
    // (receipt_no already approved for this brand), the response status
    // will be "rejected" immediately — do NOT show a review prompt in
    // that case; take the customer straight to the rejection screen.
    if (claim.status === 'rejected') {
      setSubmitting(false);
      navigateToReview(claim.id);
      return;
    }
    setSubmitting(false);
    // Submit success → maybe trigger R7 modal; the modal's dismiss/click
    // handler is responsible for the final navigation.
    await maybeTriggerReviewPrompt(claim);
  };

  return (
    <div className="relative" data-testid="claim-upload">
      {/* Gold ambient glow — matches mobile hero */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-gold/15 to-transparent -z-10"
        aria-hidden="true"
      />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        data-testid="claim-upload-back"
      >
        <ChevronLeft size={14} /> Back
      </button>

      <div className="mt-4 max-w-xl">
        <div className="text-xs uppercase tracking-widest text-gold font-semibold">
          Claim Stamp
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1.5">
          Upload Receipt
        </h1>
        <p className="text-sm text-white/60 mt-2">
          {brandQ.isLoading
            ? 'Loading…'
            : brand
              ? `Snap or pick a receipt from ${brand.name} to earn 1 stamp.`
              : 'Snap or pick your receipt to claim a stamp.'}
        </p>
      </div>

      {/* Hero circle */}
      <div
        className="mt-8 flex flex-col items-center text-center max-w-xl mx-auto"
        data-testid="claim-upload-hero"
      >
        <div className="w-32 h-32 rounded-full bg-gold/10 border border-gold/30 grid place-items-center shadow-glow-gold-sm">
          <Receipt size={48} className="text-gold" />
        </div>
        <p className="text-sm text-white/60 mt-5 max-w-md">
          Only one receipt per claim. The brand will verify it before awarding
          your stamp.
        </p>
      </div>

      {/* Primary + secondary CTAs */}
      <div className="mt-8 max-w-xl mx-auto space-y-3">
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="claim-upload-camera-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="claim-upload-gallery-input"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={submitting || !brandId}
          data-testid="claim-upload-camera"
          className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-glow-gold-sm min-h-[48px]"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
          Take Photo
        </button>
        <button
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          disabled={submitting || !brandId}
          data-testid="claim-upload-gallery"
          className="w-full inline-flex items-center justify-center gap-2 bg-transparent border border-gold/40 text-gold font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[48px]"
        >
          <Images size={16} />
          Choose from Gallery
        </button>
      </div>

      {/* Tips card */}
      <div
        className="mt-8 max-w-xl mx-auto rounded-3xl border border-white/8 bg-[#15151B] p-6"
        data-testid="claim-upload-tips"
      >
        <h3 className="font-display text-base font-semibold mb-4">
          Tips for a clear photo
        </h3>
        <ul className="space-y-3">
          {TIPS.map((tip) => (
            <li key={tip} className="flex items-start gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full bg-gold grid place-items-center mt-0.5">
                <CheckCircle2 size={12} className="text-black" />
              </span>
              <span className="text-sm text-white/70 leading-relaxed">{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Pending-verification notice (NON-NEGOTIABLE #1 reminder) */}
      <div
        className="mt-6 max-w-xl mx-auto flex items-start gap-3 rounded-2xl border border-gold/25 bg-gold/5 p-4"
        data-testid="claim-upload-pending-notice"
      >
        <ShieldCheck size={16} className="text-gold mt-0.5 shrink-0" />
        <p className="text-xs text-white/70 leading-relaxed">
          <span className="font-semibold text-gold">Pending brand verification.</span>{' '}
          Your stamp will be awarded only after the brand approves this
          receipt. You&apos;ll be notified once it&apos;s approved.
        </p>
      </div>

      {/* View my past claims link */}
      <div className="mt-6 flex justify-center">
        <Link
          to="/customer/claim-stamp/history"
          data-testid="claim-upload-history-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-gold transition-colors py-2"
        >
          <Clock size={14} className="text-gold" />
          View my past claims
          <ChevronRight size={14} className="text-white/40" />
        </Link>
      </div>

      {/* Submit overlay */}
      {submitting ? (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-4"
          data-testid="claim-upload-overlay"
        >
          <Loader2 size={48} className="text-gold animate-spin" />
          <p className="text-sm text-white">Uploading your receipt…</p>
        </div>
      ) : null}

      {/* Phase R7 — Google Review prompt, operator-approved copy. */}
      <ReviewPromptModal
        visible={!!reviewPrompt}
        prompt={reviewPrompt}
        onClose={handleReviewPromptClose}
        titleOverride="⭐ Enjoyed your visit?"
        bodyOverride="While we verify your receipt, we'd love to hear about your experience."
        primaryCtaOverride="Leave a Google Review"
        secondaryCtaOverride="Maybe Later"
        testIdPrefix="review-prompt-r7"
      />
    </div>
  );
}
